import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useOnboardingStore } from '@/store/onboarding.store';
import { getTourById } from '@/config/tours';
import { saveUserOnboarding } from '@/services/v3/onboarding';
import { useGetUserOnboardingQuery } from '@/hooks/V3/useGetUserOnboardingQuery';

/**
 * Hook to interact with the tour system
 * Provides actions and state for managing tours
 */
export const useTour = () => {
  // Get the store state and actions using getState() to avoid subscription issues
  // Then subscribe to specific state changes
  const store = useOnboardingStore;

  // Subscribe to primitive/stable values only
  const activeTourId = useOnboardingStore((state) => state.activeTourId);
  const currentStepIndex = useOnboardingStore(
    (state) => state.currentStepIndex,
  );
  const status = useOnboardingStore((state) => state.status);
  const currentTourId = useOnboardingStore(
    (state) => state.currentTour?.id ?? null,
  );
  const currentTourStepsLength = useOnboardingStore(
    (state) => state.currentTour?.steps?.length ?? 0,
  );
  const showProgress = useOnboardingStore(
    (state) => state.currentTour?.showProgress ?? true,
  );
  const allowSkip = useOnboardingStore(
    (state) => state.currentTour?.allowSkip ?? true,
  );

  // Get the full tour config only when we need it (memoized)
  const currentTour = useMemo(() => {
    if (!currentTourId) return null;
    return getTourById(currentTourId) ?? null;
  }, [currentTourId]);

  // Memoize current step
  const currentStep = useMemo(() => {
    if (
      !currentTour ||
      currentStepIndex < 0 ||
      currentStepIndex >= currentTour.steps.length
    ) {
      return null;
    }
    return currentTour.steps[currentStepIndex];
  }, [currentTour, currentStepIndex]);

  // Get completedTours reference (for sync)
  const completedToursRef = useRef(store.getState().completedTours);
  useEffect(() => {
    return store.subscribe((state) => {
      completedToursRef.current = state.completedTours;
    });
  }, []);

  // Compute derived state
  const isLastStep =
    currentTourStepsLength > 0
      ? currentStepIndex >= currentTourStepsLength - 1
      : false;
  const isFirstStep = currentStepIndex === 0;
  const isTourActive = status === 'running' && currentTourId !== null;

  // Memoize progress to prevent object recreation
  const progress = useMemo(
    () => ({
      current: currentStepIndex + 1,
      total: currentTourStepsLength,
    }),
    [currentStepIndex, currentTourStepsLength],
  );

  // Get stable action references using getState
  const startTour = useCallback((tourId: string) => {
    store.getState().startTour(tourId);
  }, []);

  const nextStep = useCallback(() => {
    store.getState().nextStep();
  }, []);

  const prevStep = useCallback(() => {
    store.getState().prevStep();
  }, []);

  const skipTourAction = useCallback(() => {
    store.getState().skipTour();
  }, []);

  const completeTourAction = useCallback(() => {
    store.getState().completeTour();
  }, []);

  const pauseTour = useCallback(() => {
    store.getState().pauseTour();
  }, []);

  const resumeTour = useCallback(() => {
    store.getState().resumeTour();
  }, []);

  const goToStep = useCallback((stepIndex: number) => {
    store.getState().goToStep(stepIndex);
  }, []);

  const markTourSeen = useCallback((tourId: string, version: number) => {
    store.getState().markTourSeen(tourId, version);
  }, []);

  const loadCompletedTours = useCallback((tours: Record<string, number>) => {
    store.getState().loadCompletedTours(tours);
  }, []);

  const resetAllTours = useCallback(() => {
    store.getState().resetAllTours();
  }, []);

  // Fetch onboarding data from backend using React Query
  const { isLoading: isOnboardingLoading, isFetched: isOnboardingFetched } =
    useGetUserOnboardingQuery({
      onSuccess: (data) => {
        if (data?.completedTours) {
          loadCompletedTours(data.completedTours);
        }
      },
    });

  // Onboarding is ready when data has been fetched from backend
  const isOnboardingReady = isOnboardingFetched;

  // shouldShowTour uses getState to get current completedTours
  const shouldShowTour = useCallback((tourId: string): boolean => {
    return store.getState().shouldShowTour(tourId);
  }, []);

  // Check if this is the first visit for a specific tour
  const isFirstVisit = useCallback(
    (tourId: string): boolean => {
      return shouldShowTour(tourId);
    },
    [shouldShowTour],
  );

  // Get tour config by ID
  const getTour = useCallback((tourId: string) => {
    return getTourById(tourId);
  }, []);

  // Start tour only if conditions are met (waits for onboarding data to be ready)
  const startTourIfNeeded = useCallback(
    (tourId: string): boolean => {
      // Don't start tour if onboarding data hasn't loaded yet
      if (!isOnboardingReady) {
        return false;
      }

      if (shouldShowTour(tourId)) {
        startTour(tourId);
        return true;
      }
      return false;
    },
    [isOnboardingReady, shouldShowTour, startTour],
  );

  const shouldWaitForTour = useCallback(
    (tourId: string): boolean => {
      if (!isOnboardingReady) return true;

      const isRequestedTourActive =
        activeTourId === tourId && status === 'running';
      if (isRequestedTourActive) return true;

      return shouldShowTour(tourId);
    },
    [activeTourId, isOnboardingReady, shouldShowTour, status],
  );

  // Sync with backend and save completed tour
  const syncAndComplete = useCallback(async () => {
    const state = store.getState();
    if (!state.activeTourId || !state.currentTour) return;

    completeTourAction();

    // Sync to backend
    try {
      const newCompletedTours = {
        ...completedToursRef.current,
        [state.activeTourId]: state.currentTour.version,
      };
      await saveUserOnboarding({
        completedTours: newCompletedTours,
      });
    } catch (error) {
      console.error('Failed to sync tour completion to backend:', error);
    }
  }, [completeTourAction]);

  // Sync with backend when skipping tour
  const syncAndSkip = useCallback(async () => {
    const state = store.getState();
    if (!state.activeTourId || !state.currentTour) return;

    skipTourAction();

    // Sync to backend
    try {
      const newCompletedTours = {
        ...completedToursRef.current,
        [state.activeTourId]: state.currentTour.version,
      };
      await saveUserOnboarding({
        completedTours: newCompletedTours,
      });
    } catch (error) {
      console.error('Failed to sync tour skip to backend:', error);
    }
  }, [skipTourAction]);

  // Keyboard navigation
  useEffect(() => {
    if (!isTourActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          syncAndSkip();
          break;
        case 'ArrowRight':
        case 'Enter':
          if (!currentStep?.nextOnAction) {
            nextStep();
          }
          break;
        case 'ArrowLeft':
          if (!isFirstStep) {
            prevStep();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isTourActive,
    currentStep?.nextOnAction,
    isFirstStep,
    nextStep,
    prevStep,
    syncAndSkip,
  ]);

  return {
    // State
    activeTourId,
    currentStepIndex,
    status,
    currentTour,
    currentStep,

    // Derived state
    isLastStep,
    isFirstStep,
    progress,
    isTourActive,
    showProgress,
    allowSkip,
    isOnboardingReady,
    isOnboardingLoading,

    // Actions
    startTour,
    startTourIfNeeded,
    nextStep,
    prevStep,
    skipTour: syncAndSkip,
    completeTour: syncAndComplete,
    pauseTour,
    resumeTour,
    goToStep,
    resetAllTours,

    // Utilities
    isFirstVisit,
    shouldShowTour,
    shouldWaitForTour,
    getTour,
    markTourSeen,
  };
};

export default useTour;
